import {HeadcountDistributionMode} from 'client/models/HeadcountDistributionMode';
import {WorkstreamHeadcountDataRead} from 'client/models/WorkstreamHeadcountDataRead';

export const GEO_FIELDS = ['geo_low_value', 'geo_mid_value', 'geo_high_value'] as const;
export const ALLOCATION_FIELDS = [
  'allocation_growth_value',
  'allocation_ci_value',
  'allocation_ktlo_value',
] as const;
export const STAFFING_FIELDS = [...GEO_FIELDS, ...ALLOCATION_FIELDS] as const;

// The base values only; adjustments are optional in the API payload and kept separately.
export type StaffingField = (typeof STAFFING_FIELDS)[number];
export type StaffingValues = Record<StaffingField, string>;

// Matches the API contract: non-negative, at most 8 integer digits and 2 decimal places.
const MAX_INTEGER_DIGITS = 8;
// Headcount is stored with at most 8 integer digits, so HC must stay below 100,000,000.
const MAX_HEADCOUNT_CENTS = 10 ** (MAX_INTEGER_DIGITS + 2);

/**
 * Parses a raw staffing amount into whole hundredths so totals can be compared exactly.
 * Returns null when the value is empty, negative, or has more than two decimal places.
 */
export const parseStaffingCents = (value: string | number): number | null => {
  const match = /^(\d*)(?:\.(\d*))?$/.exec(String(value).trim());
  if (!match) return null;
  const whole = match[1].replace(/^0+(?=\d)/, '');
  const fraction = (match[2] ?? '').replace(/0+$/, '');
  if (!match[1] && !match[2]) return null;
  if (whole.length > MAX_INTEGER_DIGITS || fraction.length > 2) return null;
  return Number(whole || '0') * 100 + Number(fraction.padEnd(2, '0'));
};

export const formatStaffingCents = (cents: number) => String(cents / 100);

const sumCents = (values: StaffingValues, fields: readonly StaffingField[]) => {
  const parsed = fields.map((field) => parseStaffingCents(values[field]));
  return parsed.every((cents) => cents != null)
    ? parsed.reduce<number>((total, cents) => total + cents!, 0)
    : null;
};

export type StaffingValidation = {
  /** HC in hundredths: the sum of Low, Mid and High, or null if any of them is invalid. */
  headcountCents: number | null;
  /** Growth + CI + KTLO in hundredths, or null if any of them is invalid. */
  allocationCents: number | null;
  invalidFields: Set<StaffingField>;
  allocationMismatch: boolean;
  headcountTooLarge: boolean;
  isValid: boolean;
};

export const validateStaffing = (values: StaffingValues): StaffingValidation => {
  const invalidFields = new Set(
    STAFFING_FIELDS.filter((field) => parseStaffingCents(values[field]) == null)
  );
  const headcountCents = sumCents(values, GEO_FIELDS);
  const allocationCents = sumCents(values, ALLOCATION_FIELDS);
  const allocationMismatch =
    headcountCents != null && allocationCents != null && headcountCents !== allocationCents;
  const headcountTooLarge = headcountCents != null && headcountCents >= MAX_HEADCOUNT_CENTS;
  return {
    headcountCents,
    allocationCents,
    invalidFields,
    allocationMismatch,
    headcountTooLarge,
    isValid: invalidFields.size === 0 && !allocationMismatch && !headcountTooLarge,
  };
};

/** Saved staffing as raw amounts, converting legacy percent-mode rows to amounts. */
export const toStaffingValues = (
  headcount?: WorkstreamHeadcountDataRead | null
): StaffingValues => {
  const toAmount = (field: StaffingField, mode: HeadcountDistributionMode) => {
    const raw = headcount?.[field] ?? '0';
    if (mode !== HeadcountDistributionMode.PERCENT) return String(raw);
    const amount = (Number(headcount?.headcount_fte) * Number(raw)) / 100;
    return Number.isFinite(amount) ? String(Math.round(amount * 100) / 100) : '0';
  };
  const geoMode = headcount?.geo_mode ?? HeadcountDistributionMode.AMOUNT;
  const allocationMode = headcount?.allocation_mode ?? HeadcountDistributionMode.AMOUNT;
  return {
    geo_low_value: toAmount('geo_low_value', geoMode),
    geo_mid_value: toAmount('geo_mid_value', geoMode),
    geo_high_value: toAmount('geo_high_value', geoMode),
    allocation_growth_value: toAmount('allocation_growth_value', allocationMode),
    allocation_ci_value: toAmount('allocation_ci_value', allocationMode),
    allocation_ktlo_value: toAmount('allocation_ktlo_value', allocationMode),
  };
};

/** Staffing inputs for a workstream whose values have not been set yet. */
export const EMPTY_STAFFING_VALUES = Object.fromEntries(
  STAFFING_FIELDS.map((field) => [field, ''])
) as StaffingValues;

export const isBlankStaffingValue = (value: string | number) => String(value).trim() === '';

/** The stored adjustment that reduces each base value in the adjusted report view. */
export const ADJUSTMENT_FIELDS = {
  geo_low_value: 'adjustment_low_value',
  geo_mid_value: 'adjustment_mid_value',
  geo_high_value: 'adjustment_high_value',
  allocation_growth_value: 'adjustment_growth_value',
  allocation_ci_value: 'adjustment_ci_value',
  allocation_ktlo_value: 'adjustment_ktlo_value',
} as const satisfies Record<StaffingField, keyof WorkstreamHeadcountDataRead>;
export type AdjustmentField = (typeof ADJUSTMENT_FIELDS)[StaffingField];

/** Adjustment inputs keyed by the base field they adjust. A blank input is not adjusted. */
export type AdjustmentValues = Record<StaffingField, string>;
export type AdjustmentPayload = Record<AdjustmentField, string | null>;

export const EMPTY_ADJUSTMENT_VALUES: AdjustmentValues = EMPTY_STAFFING_VALUES;

export const toAdjustmentValues = (
  headcount?: WorkstreamHeadcountDataRead | null
): AdjustmentValues =>
  Object.fromEntries(
    STAFFING_FIELDS.map((field) => [field, headcount?.[ADJUSTMENT_FIELDS[field]] ?? ''])
  ) as AdjustmentValues;

/** Blank inputs are sent as null, which is different from an explicit 0. */
export const toAdjustmentPayload = (values: AdjustmentValues): AdjustmentPayload =>
  Object.fromEntries(
    STAFFING_FIELDS.map((field) => [
      ADJUSTMENT_FIELDS[field],
      isBlankStaffingValue(values[field]) ? null : values[field].trim(),
    ])
  ) as AdjustmentPayload;

export type AdjustmentValidation = {
  /** Low + Mid + High adjustments in hundredths, or null if any of them is invalid. */
  headcountCents: number | null;
  /** Growth + CI + KTLO adjustments in hundredths, or null if any of them is invalid. */
  allocationCents: number | null;
  invalidFields: Set<StaffingField>;
  /** Adjustments larger than their base value, with that base value in hundredths. */
  exceededFields: Map<StaffingField, number>;
  allocationMismatch: boolean;
  isValid: boolean;
};

/**
 * Mirrors the API rules: each adjustment is at most its base value, and the Growth, CI and KTLO
 * adjustments add up to the Low, Mid and High adjustments. Blank adjustments count as zero.
 */
export const validateAdjustments = (
  base: StaffingValues,
  adjustments: AdjustmentValues
): AdjustmentValidation => {
  const filled = fillBlankStaffingValues(adjustments);
  const invalidFields = new Set(
    STAFFING_FIELDS.filter((field) => parseStaffingCents(filled[field]) == null)
  );
  const exceededFields = new Map<StaffingField, number>();
  for (const field of STAFFING_FIELDS) {
    const cents = parseStaffingCents(filled[field]);
    const baseCents = parseStaffingCents(base[field]);
    if (cents != null && baseCents != null && cents > baseCents) {
      exceededFields.set(field, baseCents);
    }
  }
  const headcountCents = sumCents(filled, GEO_FIELDS);
  const allocationCents = sumCents(filled, ALLOCATION_FIELDS);
  const allocationMismatch =
    headcountCents != null && allocationCents != null && headcountCents !== allocationCents;
  return {
    headcountCents,
    allocationCents,
    invalidFields,
    exceededFields,
    allocationMismatch,
    isValid: invalidFields.size === 0 && exceededFields.size === 0 && !allocationMismatch,
  };
};

/** Treats blank inputs as zero, so a partially filled row can be validated and saved. */
export const fillBlankStaffingValues = (values: StaffingValues): StaffingValues =>
  Object.fromEntries(
    STAFFING_FIELDS.map((field) => [
      field,
      isBlankStaffingValue(values[field]) ? '0' : values[field],
    ])
  ) as StaffingValues;

/** Whether two staffing inputs hold the same amount, so "1.5" and "1.50" match. */
export const isSameStaffingValue = (a: string | number, b: string | number) => {
  const aCents = parseStaffingCents(a);
  const bCents = parseStaffingCents(b);
  return aCents != null && bCents != null ? aCents === bCents : String(a) === String(b);
};
